import {
  Alert,
  Checkbox,
  KeywordTextInput,
  MultiSelect,
  Select,
  SelectOption,
  spacingHelper,
  Strong,
  TextInput,
  useRecentSearches,
} from '@property.com.au/ui-kit';
import { push } from '@rea-argonaut/link';
import { FunctionComponent, useReducer } from 'react';
import styled from 'styled-components';
import { isNotNullOrUndefinedValue } from '../../helpers/isNotNullOrUndefinedValue';
import { createSearchUrl } from '../../helpers/createSearchUrl';
import {
  SEARCH_KEYWORDS_DEFAULT,
  SEARCH_KEYWORDS_SEPARATOR,
  SEARCH_MIN_BATHROOMS_DEFAULT,
  SEARCH_MIN_BEDROOMS_DEFAULT,
  SEARCH_MIN_CAR_SPACES_DEFAULT,
  SEARCH_MIN_LAND_SIZE_DEFAULT,
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
  SEARCH_PROPERTY_STATUS_DEFAULT,
  SEARCH_PROPERTY_TYPE_DEFAULT_ITEM,
} from '../../helpers/defaults';
import { isPropertyStatusForRent } from '../../helpers/isPropertyStatusForRent';
import { isPropertyStatusForSale } from '../../helpers/isPropertyStatusForSale';
import { isPropertyStatusOffMarket } from '../../helpers/isPropertyStatusOffMarket';
import { createSelectOption, mapSelectOptionsToState } from '../../helpers/mapSelectOptions';
import {
  getFindSimilarImageFromUrl,
  getPolygonsFromUrl,
  getPropertyStatusFromUrl,
  getSearchFlowFromUrl,
  getSurroundingSuburbsUrl,
} from '../../helpers/queryStringHelpers/queryStringHelpers';
import {
  createInitialState,
  PropertyConfig,
  propertyConfigReducer,
  PropertyConfigReducerActions,
  SearchPage_FilterOptions,
} from '../../reducers/propertyConfigReducer';
import { SelectOptionWithBadge } from '../../types';
import { SearchPageFormLabel } from '../SearchpageFormLabel/SearchPageFormLabel';
import { SearchModal } from '../SearchModal/SearchModal';
import { isPolygonsNotDefault } from '../../helpers/searchSlugDefaultChecks';
import { getSortOptionFromUrl } from '../../helpers/queryStringHelpers/getSortOptionFromUrl';
import { getBudgetFiltersFromUrl } from '../../helpers/queryStringHelpers/getBudgetFiltersFromUrl';
import { LabelBadge } from '../LabelBadge/LabelBadge';
import { SearchRadioButtonGroup } from '../SearchRadioInputAsButtons/SearchRadioButtonGroup';
import { isPropertyStatusAllProperties } from '../../helpers/isPropertyStatusAllProperties';
import { isSearchPropertyStatus } from '../../helpers/isSearchPropertyStatus';
import { mapSelectOptionsWithBadgeToListNode } from '../../helpers/mapSelectOptionsWithBadgeToListNode';
import { searchPropertyStatusSideEffects } from '../../helpers/searchPropertyStatusSideEffects';

const Form = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacingHelper('twoExtraSmall')};

  > div {
    flex: 0 0 100%;
    margin-bottom: ${spacingHelper('medium')};
  }
`;

const PropertyStatusInputWrapper = styled.div`
  position: relative;

  .displayContainer {
    opacity: 0;
  }
`;

const SelectedPropertyStatus = styled.div`
  position: absolute;
  bottom: calc(${spacingHelper('extraSmall')}); /* bottom margin */
  left: ${spacingHelper('extraSmall')};
  margin-top: ${spacingHelper('small')};
  display: flex;
`;

const PropertyTypeSelect = styled(MultiSelect)`
  max-width: 100%;
`;

const PropertyStatusCheckboxWrapper = styled.div`
  margin-top: -${spacingHelper('small')};
`;

interface SearchPagePropertyConfigModalContentProps {
  formState: PropertyConfig;
  formStateDispatch: React.Dispatch<PropertyConfigReducerActions>;
  filterOptions: SearchPage_FilterOptions;
  searchQuery: string;
}

export const filterOutAllPropertiesOption = (
  options: SelectOptionWithBadge[],
): SelectOptionWithBadge[] =>
  options.filter((option) => !isPropertyStatusAllProperties(option.value));

export const addBetaCalloutToOffMarketOption = (
  options: SelectOptionWithBadge[],
): SelectOptionWithBadge[] =>
  options.map((option) => {
    if (isPropertyStatusOffMarket(option.value))
      return { ...option, callout: { text: 'BETA', variant: 'CALLOUT' } };
    return option;
  });

export const SearchPagePropertyConfigModalContent: FunctionComponent<
  SearchPagePropertyConfigModalContentProps
> = ({
  formState,
  formStateDispatch,
  searchQuery,
  filterOptions: {
    propertyTypes,
    propertyStatus,
    minBedrooms,
    minBathrooms,
    indoorFeatures,
    outdoorFeatures,
    ecoFriendlyFeatures,
    minCarSpaces,
  },
}) => {
  const FIELD_SIZE_VARIANT = 'medium';
  const selectedPropertyStatus = propertyStatus.find(
    (item) => item.value === formState.propertyStatus?.value,
  );

  const isOffMarketSelected = isPropertyStatusOffMarket(formState.propertyStatus?.value);
  const isForSaleSelected = isPropertyStatusForSale(formState.propertyStatus?.value);
  const isForRentSelected = isPropertyStatusForRent(formState.propertyStatus?.value);

  const isImageSimilaritySearch = Boolean(getFindSimilarImageFromUrl(searchQuery));
  const showAiKeywords = (isForSaleSelected || isForRentSelected) && !isImageSimilaritySearch;

  return (
    <Form>
      <PropertyTypeSelect
        options={propertyTypes}
        placeholder="Property type"
        id="SearchFilterPropertyType"
        onSelectedOptionsChange={(selection: SelectOption[]) => {
          formStateDispatch({
            type: 'propertyTypes',
            value: selection,
          });
        }}
        selectedOptions={formState.propertyTypes}
        label={<SearchPageFormLabel>Property type</SearchPageFormLabel>}
        renderDisplayText={(selected: SelectOption[]) =>
          selected.map((option) => option.label).join(', ')
        }
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <PropertyStatusInputWrapper>
        <Select
          options={propertyStatus.map(createSelectOption)}
          placeholder="Market status"
          id="SearchFilterPropertyStatus"
          onSelectedOptionChange={(selection?: SelectOption | null) => {
            if (!selection) return;
            formStateDispatch({ type: 'propertyStatus', value: selection });
          }}
          selectedOption={formState.propertyStatus && createSelectOption(formState.propertyStatus)}
          label={<SearchPageFormLabel>Market status</SearchPageFormLabel>}
          mapOptionToListNode={mapSelectOptionsWithBadgeToListNode(propertyStatus)}
          sizeVariant={FIELD_SIZE_VARIANT}
          dangerouslySetClassNames={{ displayContainer: 'displayContainer' }}
        />
        {selectedPropertyStatus && (
          <SelectedPropertyStatus>
            <LabelBadge
              badgeVariant={selectedPropertyStatus.badge}
              title={selectedPropertyStatus.label}
            />
          </SelectedPropertyStatus>
        )}
      </PropertyStatusInputWrapper>

      {
        <PropertyStatusCheckboxWrapper data-testid="property-status-checkbox-wrapper">
          {isOffMarketSelected && (
            <Checkbox
              label={
                <>
                  Exclude properties <Strong>without images</Strong>
                </>
              }
              checked={formState.hasImages}
              onChange={() => formStateDispatch({ type: 'hasImages', value: !formState.hasImages })}
              id="has-images"
            />
          )}

          {isForSaleSelected && (
            <Checkbox
              label={
                <>
                  Exclude properties <Strong>under offer</Strong>
                </>
              }
              checked={formState.excludeUnderOffer}
              onChange={() =>
                formStateDispatch({
                  type: 'excludeUnderOffer',
                  value: !formState.excludeUnderOffer,
                })
              }
              id="exclude-under-offer"
            />
          )}
        </PropertyStatusCheckboxWrapper>
      }
      {isImageSimilaritySearch && !isForSaleSelected && !isForRentSelected && (
        <Alert variant="warning">
          Changing the market status will remove the image filter. This feature is only available
          for properties that are <Strong>for sale</Strong> or <Strong>for rent</Strong>.
        </Alert>
      )}

      <KeywordTextInput
        variant={showAiKeywords ? ('ai' as const) : ('standard' as const)}
        onChange={(input: React.ChangeEvent<HTMLInputElement>) => {
          formStateDispatch({
            type: 'keywords',
            value: input.target.value,
          });
        }}
        defaultValue={formState.keywords}
        dataTestId="keywordsInputUnderPropertyStatusSelector"
      />
      <SearchRadioButtonGroup
        name="minBedrooms"
        label="Bedrooms"
        options={minBedrooms}
        selected={formState?.minBedrooms}
        onChange={(selection: SelectOption) => {
          formStateDispatch({
            type: 'minBedrooms',
            value: selection,
          });
        }}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <SearchRadioButtonGroup
        name="minBathrooms"
        label="Bathrooms"
        options={minBathrooms}
        selected={formState?.minBathrooms}
        onChange={(selection) => {
          formStateDispatch({
            type: 'minBathrooms',
            value: selection,
          });
        }}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <TextInput
        name="minLandSize"
        id="minLandSizeInput"
        type="number"
        step="50"
        min={SEARCH_MIN_LAND_SIZE_DEFAULT}
        autoValidate={false}
        label={<SearchPageFormLabel>Minimum land size (m²)</SearchPageFormLabel>}
        placeholder="e.g. 500"
        onChange={(input: React.ChangeEvent<HTMLInputElement>) => {
          formStateDispatch({
            type: 'minLandSize',
            value: input.target.value,
          });
        }}
        defaultValue={Number(formState.minLandSize) > 0 ? formState.minLandSize : undefined} // default is undefined rather than 0 so that the placeholder text will show
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <MultiSelect
        options={indoorFeatures}
        placeholder="Any"
        id="SearchFilterIndoorFeatures"
        onSelectedOptionsChange={(selection: SelectOption[]) => {
          formStateDispatch({
            type: 'indoorFeatures',
            value: selection,
          });
        }}
        selectedOptions={formState.indoorFeatures}
        label={<SearchPageFormLabel>Indoor features</SearchPageFormLabel>}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <MultiSelect
        options={outdoorFeatures}
        placeholder="Any"
        id="SearchFilterOutdoorFeatures"
        onSelectedOptionsChange={(selection: SelectOption[]) => {
          formStateDispatch({
            type: 'outdoorFeatures',
            value: selection,
          });
        }}
        selectedOptions={formState.outdoorFeatures}
        label={<SearchPageFormLabel>Outdoor features</SearchPageFormLabel>}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <MultiSelect
        options={ecoFriendlyFeatures}
        placeholder="Any"
        id="SearchFilterEcoFriendlyFeatures"
        onSelectedOptionsChange={(selection: SelectOption[]) => {
          formStateDispatch({
            type: 'ecoFriendlyFeatures',
            value: selection,
          });
        }}
        selectedOptions={formState.ecoFriendlyFeatures}
        label={<SearchPageFormLabel>Eco friendly features</SearchPageFormLabel>}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
      <Select
        options={minCarSpaces}
        placeholder="Car spaces"
        id="SearchFilterMinCarSpaces"
        onSelectedOptionChange={(selection?: SelectOption | null) => {
          if (selection) {
            formStateDispatch({
              type: 'minCarSpaces',
              value: selection,
            });
          }
        }}
        selectedOption={formState.minCarSpaces}
        label={<SearchPageFormLabel>Car spaces</SearchPageFormLabel>}
        sizeVariant={FIELD_SIZE_VARIANT}
      />
    </Form>
  );
};

interface PropertyConfigModalProps {
  onClose: () => void;
  title: string;
  subtitle: string;
  filterOptions: SearchPage_FilterOptions;
  searchQuery: string;
  saveRecentLocationSearch: ReturnType<typeof useRecentSearches>['saveRecentLocationSearch'];
}

export const PropertyConfigModal: FunctionComponent<PropertyConfigModalProps> = ({
  onClose,
  title,
  subtitle,
  filterOptions,
  searchQuery,
  saveRecentLocationSearch,
}) => {
  const [formState, formStateDispatch] = useReducer(
    propertyConfigReducer,
    createInitialState(searchQuery, { ...filterOptions }),
  );
  const onConfirm = () => {
    const propertyStatusFromUrl = getPropertyStatusFromUrl(searchQuery);
    const { minPrice: minPriceFromUrl, maxPrice: maxPriceFromUrl } =
      getBudgetFiltersFromUrl(searchQuery);
    const surroundingSuburbsFromUrl = getSurroundingSuburbsUrl(searchQuery);
    const sortOptionFromUrl = getSortOptionFromUrl(searchQuery);
    const findSimilarImageFromUrl = getFindSimilarImageFromUrl(searchQuery);
    const isGeoShapeBasedSearch =
      isPolygonsNotDefault(getPolygonsFromUrl(searchQuery)) ||
      getSearchFlowFromUrl(searchQuery) === 'commute';

    const {
      propertyTypes,
      propertyStatus,
      minBedrooms,
      minBathrooms,
      minLandSize,
      minCarSpaces,
      indoorFeatures,
      outdoorFeatures,
      ecoFriendlyFeatures,
      keywords,
    } = formState;

    const newPropertyStatus =
      isNotNullOrUndefinedValue(propertyStatus) && isSearchPropertyStatus(propertyStatus.value)
        ? propertyStatus.value
        : SEARCH_PROPERTY_STATUS_DEFAULT;

    createSearchUrl({
      currentUrl: searchQuery,
      updates: {
        pageNumber: '',
        propertyTypes: mapSelectOptionsToState(propertyTypes, SEARCH_PROPERTY_TYPE_DEFAULT_ITEM),
        propertyStatus: newPropertyStatus,
        minBedrooms: minBedrooms?.value ?? SEARCH_MIN_BEDROOMS_DEFAULT,
        minBathrooms: minBathrooms?.value ?? SEARCH_MIN_BATHROOMS_DEFAULT,
        minLandSize: minLandSize || SEARCH_MIN_LAND_SIZE_DEFAULT,
        indoorFeatures: mapSelectOptionsToState(
          indoorFeatures,
          SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          SEARCH_KEYWORDS_SEPARATOR,
        ),
        outdoorFeatures: mapSelectOptionsToState(
          outdoorFeatures,
          SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          SEARCH_KEYWORDS_SEPARATOR,
        ),
        ecoFriendlyFeatures: mapSelectOptionsToState(
          ecoFriendlyFeatures,
          SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          SEARCH_KEYWORDS_SEPARATOR,
        ),
        minCarSpaces: minCarSpaces?.value ?? SEARCH_MIN_CAR_SPACES_DEFAULT,
        keywords: keywords ?? SEARCH_KEYWORDS_DEFAULT,
        excludeUnderOffer: formState.excludeUnderOffer ? 'true' : 'false',
        excludeDepositTaken: formState.excludeDepositTaken ? 'true' : 'false',
        hasImages: formState.hasImages ? 'true' : 'false',

        // Update the url with side effects when PropertyStatus Changes
        ...searchPropertyStatusSideEffects({
          newPropertyStatus,
          minPrice: minPriceFromUrl,
          maxPrice: maxPriceFromUrl,
          surroundingSuburbs: surroundingSuburbsFromUrl,
          previousPropertyStatus: propertyStatusFromUrl,
          currentSortOption: sortOptionFromUrl,
          findSimilar: findSimilarImageFromUrl,
          isGeoShapeBasedSearch,
        }),
      },
      historyPushFn: push,
      saveRecentLocationSearch,
    });

    onClose();
  };
  return (
    <SearchModal opened onCancel={onClose} onConfirm={onConfirm} title={title} subtitle={subtitle}>
      <SearchPagePropertyConfigModalContent
        formState={formState}
        formStateDispatch={formStateDispatch}
        filterOptions={filterOptions}
        searchQuery={searchQuery}
      />
    </SearchModal>
  );
};
